// CCI pre-trip checklist catalog + severity rules (CONF-20260716)

export type ChecklistItemStatus = 'pass' | 'fail' | 'na' | 'unset';

export type ChecklistSeverity = 'critical' | 'non_critical';

export type ChecklistGroupId = 'safety' | 'fluids' | 'cabin' | 'ops';

export interface ChecklistItemDef {
  id: string;
  label: string;
  severity: ChecklistSeverity;
  group: ChecklistGroupId;
  /** When true, N/A is allowed (vehicle-type dependent items). */
  allowNa?: boolean;
}

export const CCI_CHECKLIST_GROUPS: readonly { id: ChecklistGroupId; label: string }[] = [
  { id: 'safety', label: 'Safety & wheels' },
  { id: 'fluids', label: 'Fluids & engine' },
  { id: 'cabin', label: 'Cabin & comfort' },
  { id: 'ops', label: 'Ops & clean' },
] as const;

/** Paper form CCI_000003 BEFORE column items. */
export const CCI_CHECKLIST_ITEMS: readonly ChecklistItemDef[] = [
  { id: 'wheel_security_nut', label: 'Wheel security / Nut', severity: 'critical', group: 'safety' },
  { id: 'tyre_pressure', label: 'Tyre Pressure', severity: 'critical', group: 'safety' },
  { id: 'lights_indicators', label: 'All lights and indicators', severity: 'critical', group: 'safety' },
  { id: 'fire_extinguisher', label: 'Fire extinguisher', severity: 'critical', group: 'safety' },
  { id: 'spare_tyre_jack', label: 'Space tyre and Jack', severity: 'non_critical', group: 'safety' },
  { id: 'wiper_blades', label: 'Wiper blade operation', severity: 'non_critical', group: 'safety' },

  { id: 'engine_oil', label: 'Engine oil', severity: 'critical', group: 'fluids' },
  { id: 'power_steering_fluid', label: 'P/Steering Hydraulic fluid level', severity: 'critical', group: 'fluids' },
  { id: 'belts_hoses', label: 'All Belt & Hoses', severity: 'critical', group: 'fluids' },
  { id: 'air_tanks', label: 'Check air level in tanks', severity: 'critical', group: 'fluids', allowNa: true },
  { id: 'clutch_fluid', label: 'CLUTCH FLUID LEVEL', severity: 'critical', group: 'fluids', allowNa: true },
  { id: 'coolant_water', label: 'Water (coolant)', severity: 'non_critical', group: 'fluids' },
  { id: 'batteries', label: 'Batteries', severity: 'non_critical', group: 'fluids' },
  { id: 'fuel_level_check', label: 'Fuel Level', severity: 'non_critical', group: 'fluids' },

  { id: 'microphone', label: 'Microphone', severity: 'non_critical', group: 'cabin' },
  { id: 'heating_ventilation', label: 'Heating and ventilation system', severity: 'non_critical', group: 'cabin' },
  { id: 'cooling_ventilation', label: 'Cooling and ventilation system', severity: 'non_critical', group: 'cabin' },
  { id: 'seat_curtain_tidy', label: 'SEAT UP AND CURTAIN TIDY', severity: 'non_critical', group: 'cabin' },

  { id: 'bus_window_clean', label: 'Bus Clean & Window clean', severity: 'non_critical', group: 'ops' },
  { id: 'no_passenger_left', label: 'Check No Passenger left on bus', severity: 'non_critical', group: 'ops', allowNa: true },
] as const;

export const CCI_CRITICAL_ITEM_IDS: ReadonlySet<string> = new Set(
  CCI_CHECKLIST_ITEMS.filter((i) => i.severity === 'critical').map((i) => i.id)
);

/**
 * AFTER column uses the same item catalog as BEFORE (paper CCI).
 * Soft gate only — failures do not block trip arrival.
 */
export const CCI_AFTER_CHECKLIST_ITEMS: readonly ChecklistItemDef[] = CCI_CHECKLIST_ITEMS;

export type BodyView = 'front' | 'back' | 'left' | 'right';

export const CCI_BODY_VIEWS: readonly { id: BodyView; label: string }[] = [
  { id: 'left', label: 'Left side' },
  { id: 'right', label: 'Right side' },
  { id: 'front', label: 'Front' },
  { id: 'back', label: 'Back' },
] as const;

/** Placeholder declaration until client legal copy is supplied. */
export const CCI_DECLARATION_TEXT_VER = 'cci-en-v1-placeholder';

export const CCI_DECLARATION_TEXT = [
  'I confirm that I have completed the pre-departure vehicle inspection listed above.',
  'I am fit for duty and the vehicle is safe to operate for the assigned work,',
  'except where defects are noted and accepted under company procedures.',
].join(' ');

export function checklistItemLabel(id: string): string {
  return CCI_CHECKLIST_ITEMS.find((i) => i.id === id)?.label ?? id;
}

export function countFailedItems(
  items: { id: string; status: string }[] | null | undefined
): number {
  if (!items?.length) return 0;
  return items.filter((i) => i.status === 'fail').length;
}

export function itemsAnsweredCount(
  items: { id: string; status: string }[] | null | undefined
): number {
  if (!items?.length) return 0;
  return items.filter((i) => i.status === 'pass' || i.status === 'fail' || i.status === 'na').length;
}
